// Helper function for deep equality check of two objects
export const isEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== "object" ||
    obj1 === null ||
    typeof obj2 !== "object" ||
    obj2 === null
  )
    return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) return false;
  }
  return true;
};

/**
 * Converts backend parameter values to user-friendly display values
 * @param {string} parameterType - The type of parameter (e.g., 'parser', 'retrieval_method', etc.)
 * @param {string} backendValue - The backend value to convert
 * @returns {string} - The user-friendly display value
 */
export const convertBackendValueToDisplay = (parameterType, backendValue) => {
  if (!backendValue) return backendValue;

  const mappings = {
    // Document Parser mappings
    parser: {
      recursive_character_text_splitter: "Code Based Parser",
      llama_parse: "Llama Parse",
      unstructuredio: "Unstructured.io",
      "gpt-4.1": "GPT-4.1",
      "gpt-4.1-mini": "GPT-4.1 Mini",
      "gemini-2.0-flash": "Gemini 2.0 Flash",
      "gemini-2.5-pro-preview-06-05": "Gemini 2.5 Pro Preview",
      "gemini-2.5-flash-preview-05-20": "Gemini 2.5 Flash Preview",
      "claude-3-7-sonnet-latest": "Claude 3.7 Sonnet Latest",
      "claude-sonnet-4-20250514": "Claude Sonnet 4",
    },

    // Retrieval Method mappings
    retrieval_method: {
      basic: "Basic",
      "sentence-window": "Chunk Window",
      context_retrieval: "Contextual Retrieval",
      "multi-query": "Multi-Query",
      hyde: "HyDE",
      "complex-to-simple-query": "Deconstruction",
      "graphrag+basic": "GraphRAG With Standard Retrieval",
      "graphrag+sentence-window": "GraphRAG With Chunk Window",
      "graphrag+context_retrieval": "GraphRAG With Contextual Retrieval",
    },

    // Rerank Method mappings
    rerank_method: {
      none: "None",
      cohere: "Cohere Rerank",
      llm_rerank: "LLM Rerank",
      pinecone: "Pinecone",
      jina: "Jina",
      nvidia: "Nvidia",
      contextualai: "Contextual AI",
      llamarank: "LlamaRank",
    },

    // Embedding Model mappings
    embedding_model: {
      "text-embedding-ada-002": "text-embedding-ada-002",
      "text-embedding-3-small": "text-embedding-3-small",
      "text-embedding-3-large": "text-embedding-3-large",
      "voyage-law-2": "Voyage Law 2",
      "voyage-finance-2": "Voyage Finance 2",
      "voyage-code-3": "Voyage Code 3",
      "voyage-3.5": "Voyage 3.5",
      "embed-v4.0": "Embed 4.0",
      "fine-tuned": "Fine tuned model",
    },

    // Metric Type mappings
    metric: {
      cosine: "Cosine",
      dotproduct: "Dot Product",
      euclidean: "Euclidean",
      hybrid: "Hybrid",
      "cosine+sparse": "Cosine + Sparse",
    },

    // Query Preprocessing Method mappings
    query_preprocess_method: {
      basic: "None",
      multi_query: "Multi-phrasing",
      hyde: "HyDE",
      complex_to_simple: "Deconstruction",
      query_expansion: "Expansion",
      iterative_deconstruction: "Iterative Deconstruction",
    },

    // Chunk Size mappings
    chunksize: {
      300: "300",
      700: "700",
      1800: "1800",
      4200: "4200",
    },

    // Top K mappings (these are numeric values, so we return them as-is)
    top_k: {
      1: "1",
      5: "5",
      10: "10",
      15: "15",
      20: "20",
      25: "25",
      30: "30",
      35: "35",
      40: "40",
    },
  };

  const parameterMapping = mappings[parameterType];
  if (!parameterMapping) {
    return backendValue; // Return original value if no mapping found
  }

  return parameterMapping[backendValue] || backendValue;
};
